/* Addresses CRUD — backed by the customer-PWA infill endpoints under
 * `/user/addresses`.
 *
 * Wire contract:
 *   - Backend uses snake_case + split fields (line1, line2, pincode,
 *     city_id, lat, lng, delivery_instructions, is_default).
 *   - Existing PWA screens currently capture only `{label, full, isDefault}`
 *     (single-line address string + isDefault toggle).
 *   - This module translates between the two: snake → camel on reads,
 *     camel → snake on writes. The `full` field is treated as `line1` until
 *     the AddressEditor screen is upgraded to capture pincode + city + a
 *     map-picked lat/lng.
 *
 * Known limitation for now:
 *   Creating an address against the real backend REQUIRES city_id + pincode
 *   + lat + lng. The current AddressEditor doesn't capture those — so a
 *   create against a real backend will return 400 until the screen is
 *   upgraded. Mock mode is unaffected.
 */

import { api, mockOr } from '../lib/api.js';
import { D } from '../mock-data.js';

/** Backend Address row → screen shape (camelCase). */
function toCamel(a) {
  return {
    id: a.id,
    userId: a.user_id,
    label: a.label,
    line1: a.line1,
    line2: a.line2,
    buildingName: a.building_name,
    pincode: a.pincode,
    cityId: a.city_id,
    lat: a.lat,
    lng: a.lng,
    deliveryInstructions: a.delivery_instructions,
    isDefault: a.is_default,
    // Convenience for screens still reading a single-line representation.
    full: [a.line1, a.line2, a.building_name, a.pincode].filter(Boolean).join(', '),
    createdAt: a.created_at,
    updatedAt: a.updated_at,
  };
}

/** Screen shape (camelCase) → backend NewAddress / UpdateAddress (snake_case).
 *  Drops `id`, `userId`, `createdAt`, `updatedAt`, `full` — server-managed.
 *  `make_default` is the create-time flag (a separate POST endpoint handles
 *  promotion of existing rows). */
function toSnake(a, { create = false } = {}) {
  const out = {};
  if (a.label !== undefined) out.label = a.label;
  // Accept either `line1` or `full` from the legacy single-line editor.
  if (a.line1 !== undefined) out.line1 = a.line1;
  else if (a.full !== undefined) out.line1 = a.full;
  if (a.line2 !== undefined) out.line2 = a.line2;
  if (a.buildingName !== undefined) out.building_name = a.buildingName;
  if (a.pincode !== undefined) out.pincode = a.pincode;
  if (a.cityId !== undefined) out.city_id = a.cityId;
  if (a.lat !== undefined) out.lat = a.lat;
  if (a.lng !== undefined) out.lng = a.lng;
  if (a.deliveryInstructions !== undefined) out.delivery_instructions = a.deliveryInstructions;
  // `make_default` is only meaningful at creation; UPDATEs use the dedicated
  // POST /:id/default endpoint to flip the flag.
  if (create && a.isDefault) out.make_default = true;
  return out;
}

export async function listAddresses() {
  return mockOr(
    async () => {
      const rows = await api.get('/user/addresses');
      return Array.isArray(rows) ? rows.map(toCamel) : [];
    },
    () => D.addresses,
  );
}

export async function createAddress(addr) {
  return mockOr(
    async () => {
      const row = await api.post('/user/addresses', toSnake(addr, { create: true }), {
        idempotencyScope: 'addr.create',
      });
      return toCamel(row);
    },
    () => ({ ...addr, id: 'a' + Date.now() }),
  );
}

export async function updateAddress(id, patch) {
  return mockOr(
    async () => {
      const row = await api.patch(`/user/addresses/${id}`, toSnake(patch));
      return toCamel(row);
    },
    () => ({ id, ...patch }),
  );
}

export async function deleteAddress(id) {
  return mockOr(
    () => api.del(`/user/addresses/${id}`),
    () => ({ ok: true }),
  );
}

/** Promote an existing address to default. The screen still optimistically
 *  flips local state — this call confirms with the server (returns the
 *  updated row in camelCase). */
export async function setDefaultAddress(id) {
  return mockOr(
    async () => {
      const row = await api.post(`/user/addresses/${id}/default`);
      return toCamel(row);
    },
    () => ({ ok: true }),
  );
}
